import React from 'react';

// Individual positioning for each trophy's nameplate
const NAMEPLATE_POSITIONS = {
  liberty: { 
    bronze: { bottom: '1.6rem', width: '70%', offsetX: '-0.5rem' },
    silver: { bottom: '1.5rem', width: '70%', offsetX: '0' },
    gold: { bottom: '1.3rem', width: '70%', offsetX: '0.5rem' }
  },
  sword: { 
    bronze: { bottom: '1.4rem', width: '70%', offsetX: '0.1rem' },
    silver: { bottom: '1.5rem', width: '70%', offsetX: '0' },
    gold: { bottom: '1.7rem', width: '70%', offsetX: '0.0rem' }
  },
  shield: { 
    bronze: { bottom: '1.2rem', width: '70%', offsetX: '0.2rem' },
    silver: { bottom: '2.0rem', width: '70%', offsetX: '0.6rem' },
    gold: { bottom: '1.7rem', width: '70%', offsetX: '0.0rem' }
  },
  cup: { 
    bronze: { bottom: '0.8rem', width: '70%', offsetX: '0.6rem' },
    silver: { bottom: '1.3rem', width: '70%', offsetX: '0.0rem' },
    gold: { bottom: '1.9rem', width: '70%', offsetX: '0.1rem' }
  },
  heart: { 
    bronze: { bottom: '1.5rem', width: '70%', offsetX: '0.1rem' },
    silver: { bottom: '1.6rem', width: '70%', offsetX: '0.0rem' },
    gold: { bottom: '1.8rem', width: '70%', offsetX: '0.4rem' }
  },
  carrot_basket: { 
    silver: { bottom: '1.8rem', width: '70%', offsetX: '0.0rem' },
    gold: { bottom: '1.8rem', width: '70%', offsetX: '0.1rem' }
  }
};

/**
 * TrophyDisplay component - renders a trophy with nameplate text overlay
 * This component handles auto-sizing text to fit within the nameplate area
 */
const TrophyDisplay = ({ 
  design, 
  color, 
  nameplateText,
  fontSize = 0.75,
  count = null, 
  size = 'medium',
  className = '' 
}) => {
  const sizeClasses = {
    small: 'w-24 h-24',
    medium: 'w-32 h-32',
    large: 'w-48 h-48'
  };

  const textSizes = {
    small: { base: '0.5rem', long: '0.4rem' },
    medium: { base: '0.65rem', long: '0.55rem' },
    large: { base: '0.75rem', long: '0.65rem' }
  };

  // Scale custom font size based on trophy size
  const getScaledFontSize = () => {
    if (size === 'small') return `${fontSize * 0.7}rem`;
    if (size === 'medium') return `${fontSize * 0.85}rem`;
    return `${fontSize}rem`; // large uses full custom size
  };

  // Get text color based on trophy color for engraved appearance
  const getTextColor = () => {
    switch (color) {
      case 'gold':
        return '#8B6914'; // Dark gold for contrast
      case 'silver':
        return '#4A4A4A'; // Dark gray for contrast
      case 'bronze':
        return '#6B4423'; // Dark bronze for contrast
      default:
        return '#333333';
    }
  };

  const getTrophyImagePath = () => {
    return `/trophies/${design}_${color}.png`;
  };

  // Wrap text at 10 characters per line
  const wrapText = (text, maxCharsPerLine = 10) => {
    if (!text) return '';
    const words = text.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length <= maxCharsPerLine) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        // If single word is longer than max, split it
        if (word.length > maxCharsPerLine) {
          let remaining = word;
          while (remaining.length > maxCharsPerLine) {
            lines.push(remaining.substring(0, maxCharsPerLine));
            remaining = remaining.substring(maxCharsPerLine);
          }
          currentLine = remaining;
        } else {
          currentLine = word;
        }
      }
    });
    
    if (currentLine) lines.push(currentLine);
    return lines.join('\n');
  };

  return (
    <div className={`relative inline-block ${className}`} style={{ background: 'transparent' }}>
      {/* Trophy Image */}
      <img
        src={getTrophyImagePath()}
        alt={`${design} ${color} trophy`}
        className={`${sizeClasses[size]} object-contain`}
        style={{
          background: 'transparent'
        }}
        onError={(e) => {
          console.error(`Failed to load trophy: ${getTrophyImagePath()}`);
          e.target.style.display = 'none';
        }}
      />
      
      {/* Nameplate Text Overlay */}
      {nameplateText && (() => {
        // Get position for this specific trophy design and color
        const basePosition = NAMEPLATE_POSITIONS[design]?.[color] || { bottom: '1.5rem', width: '70%', offsetX: '0' };
        
        // Scale position based on size
        const scalePosition = (pos) => {
          const bottomValue = parseFloat(pos.bottom);
          const widthValue = parseFloat(pos.width);
          const offsetXValue = pos.offsetX ? parseFloat(pos.offsetX) : 0;
          
          if (size === 'small') {
            return { 
              bottom: `${bottomValue * 0.5}rem`, 
              width: `${Math.min(widthValue + 10, 85)}%`,
              offsetX: `${offsetXValue * 0.5}rem`
            };
          } else if (size === 'medium') {
            return { 
              bottom: `${bottomValue * 0.7}rem`, 
              width: `${Math.min(widthValue + 5, 80)}%`,
              offsetX: `${offsetXValue * 0.7}rem`
            };
          }
          return { ...pos, offsetX: pos.offsetX || '0' }; // large size uses original
        };
        
        const position = scalePosition(basePosition);
        const horizontalOffset = position.offsetX || '0';
        
        return (
          <div 
            className="absolute left-1/2"
            style={{
              transform: `translate(calc(-50% + ${horizontalOffset}), 0)`,
              bottom: position.bottom,
              width: position.width,
              fontSize: getScaledFontSize(),
              lineHeight: '1.1',
              maxHeight: size === 'small' ? '1.5rem' : size === 'medium' ? '2rem' : '2.5rem',
              overflow: 'hidden'
            }}
          >
            <div 
              className="font-bold text-center px-1"
              style={{
                color: getTextColor(),
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                whiteSpace: 'pre-line',
                overflow: 'hidden'
              }}
            >
              {wrapText(nameplateText, 10)}
            </div>
          </div>
        );
      })()}
      
      {/* Count Badge (for multiple quiz-outs or wins) */}
      {count && count > 1 && (() => {
        // Get badge color based on trophy color
        const getBadgeColor = () => {
          switch (color) {
            case 'gold':
              return 'bg-yellow-500 border-yellow-600';
            case 'silver':
              return 'bg-gray-400 border-gray-500';
            case 'bronze':
              return 'bg-amber-600 border-amber-700';
            default:
              return 'bg-gray-500 border-gray-600';
          }
        };
        
        return (
          <div 
            className={`absolute -top-2 -right-2 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-lg border-2 border-white ${getBadgeColor()}`}
          >
            {count}
          </div>
        );
      })()}
    </div>
  );
};

export default TrophyDisplay;
